import React, { useEffect, useState } from "react";
import { IconX } from "@tabler/icons-react";
import { getCases, launchExperiment, pollJob } from "../services/api";
import { EMU, EMU_BY_SET, MODEL_SET_OPTIONS, SYSTEMS, couplesWith } from "../constants";
import VendorBadge, { vendorOfSystem } from "../components/VendorBadge";
import CommitLink from "../components/CommitLink";
import { PREPARING_LABEL, promptsRefParam } from "../promptsCommit";
import { failedRuns, launchError, nextName, statusTone, upsertLaunch } from "../launchModel";

// Persona selection: one radio combining the server's style_policy + (for fixed) the single persona.
const PERSONA_OPTIONS = [
  { id: "cycle", label: "Deterministic cycle", hint: "rotates baseline/lower/upper by repeat" },
  { id: "random", label: "Random cycle", hint: "seeded-random persona per run" },
  { id: "baseline", label: "Baseline only" },
  { id: "upper", label: "Upper only" },
  { id: "lower", label: "Lower only" },
];
const FIXED_PERSONAS = ["baseline", "upper", "lower"];

// Mari "mode" (clinic_service_type) — sets the graph preset for Mari runs. Mari-only (the history
// baselines have no graph), but recorded on every run for provenance. Default urgent for Research.
const SERVICE_TYPE_OPTIONS = [
  { id: "urgent", label: "Urgent" },
  { id: "non-urgent", label: "Non-urgent" },
];

export default function RunExperiment() {
  const [cases, setCases] = useState([]);
  const [name, setName] = useState("pilot-experiment");
  const [emu, setEmu] = useState("xai");   // default: Grok — SUT-decoupled, never couples
  const [systems, setSystems] = useState(["mari"]);
  const [caseIds, setCaseIds] = useState([]);
  const [repeats, setRepeats] = useState(3);
  const [persona, setPersona] = useState("cycle");
  const [serviceType, setServiceType] = useState("urgent");   // Mari "mode" — default urgent for Research
  const [modelSet, setModelSet] = useState("current");        // study model set — current | previous
  const [couplingAllowed, setCouplingAllowed] = useState(false);
  // Prompt pinning: optional florence-prompts SHA or branch for the Mari runs. Empty = current main.
  const [promptsRef, setPromptsRef] = useState("");
  // Experiments launched from this page, newest first: [{id, name, job}]. Each one polls on its own.
  const [launches, setLaunches] = useState([]);
  // The overlay banner: {name, ref} while a launch is sent and its prompts load. Launch is disabled only
  // then, so the next experiment can start as soon as the prompts are ready.
  const [preparing, setPreparing] = useState(null);
  const [launchErr, setLaunchErr] = useState(null);

  useEffect(() => { getCases().then((d) => setCases(d.groups.flatMap((g) => g.cases))); }, []);

  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const total = systems.length * caseIds.length * repeats;
  // Cases list columns: checkbox | chief complaint (fixed 150px) | target diagnosis | vignette snippet.
  // Diagnosis trimmed 20% (1.4->1.12fr) with the total fr held constant (2.6->2.88fr) so the freed width
  // goes to the vignette and the dx column is a true 20% narrower.
  const CASE_COLS = "auto 150px 1.12fr 2.88fr";
  const coupledSystems = systems.filter((s) => couplesWith(s, emu));
  const blocked = coupledSystems.length > 0 && !couplingAllowed;

  const launch = async () => {
    const launchName = name;
    setLaunchErr(null);
    setPreparing({ name: launchName, ref: promptsRefParam(promptsRef) || "main" });
    const isFixed = FIXED_PERSONAS.includes(persona);
    // Auto-seed: one fresh seed per launch (recorded with every run for reproducibility). It only
    // feeds the random-persona hash + provenance, so a launch-time value is all it needs to be.
    const seed = `${Date.now()}${Math.floor(Math.random() * 1000)}`;
    let jobId;
    try {
      ({ job_id: jobId } = await launchExperiment({ name: launchName, systems, case_ids: caseIds, repeats,
        style_policy: isFixed ? "fixed" : persona, seed, coupling_allowed: couplingAllowed,
        active_styles: isFixed ? [persona] : undefined, service_type: serviceType,
        model_set: modelSet, prompts_ref: promptsRefParam(promptsRef),
        emulator_provider: emu, emulator_model: EMU_BY_SET[modelSet][emu] }));
    } catch (e) {
      setPreparing(null);
      setLaunchErr(`Launch failed: ${e?.response?.data?.detail || e.message}`);
      return;
    }
    setName(nextName(launchName));
    setLaunches((ls) => upsertLaunch(ls, jobId, launchName, { status: "preparing", done: 0, total }));
    // Release the banner + button once, on the first tick past "preparing"; the poll goes on for the row.
    let released = false;
    const release = (err) => {
      if (released) return;
      released = true;
      setPreparing(null);
      if (err) setLaunchErr(`${launchName}: ${err}`);
    };
    pollJob(jobId, { interval: 3000, onTick: (jb) => {
      setLaunches((ls) => upsertLaunch(ls, jobId, launchName, jb));
      if (jb.status !== "preparing") release(launchError(jb));
    } }).catch((e) => release(`lost track of the job (${e.message}). Check the navbar or Runs & Storage.`));
  };

  return (
    <>
      <div className="page-head"><h2>Experiment Setup</h2></div>
      <div className="card">
        <label className="f">Experiment name</label>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} style={{ maxWidth: 420 }} />

        <div className="hr" />
        <label className="f">Configuration</label>
        <div className="row" style={{ alignItems: "flex-start" }}>
          {/* History Systems — which systems to drive (Mari + the history-LLM baselines) */}
          <div className="col">
            <label className="f">History Systems</label>
            {SYSTEMS.map((s) => (
              <React.Fragment key={s}>
                <label className="checkline">
                  <input type="checkbox" checked={systems.includes(s)} onChange={() => toggle(systems, setSystems, s)} />
                  <VendorBadge vendor={vendorOfSystem(s)} />
                  {couplesWith(s, emu) && <span className="chip bad">couples with emulator</span>}
                </label>
                {/* Service type — Mari "mode" (clinic_service_type); sets the graph preset. Mari-only, so it
                    lives as a sub-option under the Mari checkbox. Dimmed when Mari isn't selected. */}
                {s === "mari" && (
                  <div style={{ marginLeft: 26, opacity: systems.includes("mari") ? 1 : 0.5 }}>
                    {SERVICE_TYPE_OPTIONS.map((o) => (
                      <label className="checkline" key={o.id} style={{ fontSize: 13 }}>
                        <input type="radio" name="service_type" checked={serviceType === o.id}
                               disabled={!systems.includes("mari")} onChange={() => setServiceType(o.id)} />
                        <span>{o.label}</span>
                      </label>
                    ))}
                    {/* Prompts commit — every Mari run of the experiment uses this ONE florence-prompts
                        commit (resolved at launch). Empty = the commit current on main. */}
                    <label className="f" htmlFor="prompts-ref" style={{ marginTop: 6 }}>Prompts commit</label>
                    <input id="prompts-ref" type="text" className="mono" value={promptsRef}
                           placeholder="current main" disabled={!systems.includes("mari")}
                           onChange={(e) => setPromptsRef(e.target.value)} style={{ width: 180 }} />
                    <div className="muted" style={{ fontSize: 11.5, marginTop: 3, maxWidth: 200 }}>
                      SHA or branch. Empty = current main.
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Emulator — single select over the cheapest per-vendor emulator model */}
          <div className="col">
            <label className="f">Emulator</label>
            {Object.keys(EMU).map((p) => (
              <label className="checkline" key={p}>
                <input type="radio" name="emulator" checked={emu === p} onChange={() => setEmu(p)} />
                <VendorBadge vendor={p} />
              </label>
            ))}
          </div>

          {/* Persona — single select combining style_policy + (for fixed) the one persona */}
          <div className="col">
            <label className="f">Persona</label>
            {PERSONA_OPTIONS.map((o) => (
              <label className="checkline" key={o.id}>
                <input type="radio" name="persona" checked={persona === o.id} onChange={() => setPersona(o.id)} />
                <span>{o.label}{o.hint && <span className="muted"> ({o.hint})</span>}</span>
              </label>
            ))}
          </div>

          {/* Model set — which frozen study set the history SUTs + emulator run (resolved server-side).
              Option B: Mari's internal runtime models and the evaluator ALWAYS run current. */}
          <div className="col">
            <label className="f">Model set</label>
            {MODEL_SET_OPTIONS.map((o) => (
              <label className="checkline" key={o.id}>
                <input type="radio" name="model_set" checked={modelSet === o.id} onChange={() => setModelSet(o.id)} />
                <span>{o.label}</span>
              </label>
            ))}
            {modelSet === "previous" && (
              <div className="muted" style={{ fontSize: 12, marginTop: 4, maxWidth: 230 }}>
                Applies to the history baselines and the emulator. Mari runs its current runtime
                models; the evaluator stays current.
              </div>
            )}
          </div>
        </div>

        <div className="hr" />
        <label className="f">Cases</label>
        <div style={{ maxHeight: 180, overflowY: "auto", border: "1px solid var(--line)", borderRadius: 8, padding: 8 }}>
          {cases.map((c) => (
            <label className="checkline" key={c.id}
                   style={{ display: "grid", gridTemplateColumns: CASE_COLS, gap: 10, alignItems: "start" }}>
              <input type="checkbox" checked={caseIds.includes(c.id)} onChange={() => toggle(caseIds, setCaseIds, c.id)} />
              <span className="mono">{c.chief_complaint}</span>
              <span>{(c.ground_truth_differential && c.ground_truth_differential[0]) || <span className="muted">—</span>}</span>
              <span className="muted">{(c.vignette || "").slice(0, 120)}{(c.vignette || "").length > 120 ? "…" : ""}</span>
            </label>
          ))}
          {cases.length === 0 && <span className="muted">No cases — add some in the Case Library.</span>}
        </div>

        <div className="hr" />
        <label className="f">Repeats</label>
        <input type="number" min="1" value={repeats}
          onChange={(e) => setRepeats(parseInt(e.target.value || "0", 10))}
          style={{ width: 140, fontSize: 22, fontWeight: 600, padding: "10px 14px" }} />
        <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>Seed is auto-generated per launch and recorded with each run.</div>

        {coupledSystems.length > 0 && (
          <label className="checkline" style={{ marginTop: 10 }}>
            <input type="checkbox" checked={couplingAllowed} onChange={(e) => setCouplingAllowed(e.target.checked)} />
            Allow model coupling (exploratory) — coupled runs are tagged &amp; excluded from the confirmatory pool
          </label>
        )}
        {blocked && <div className="banner bad" style={{ marginTop: 10 }}>Anti-coupling: {coupledSystems.join(", ")} share the emulator's family. Enable the exploratory override or drop those systems.</div>}

        <div className="matrix">
          <div className="mtile"><b>{systems.length}</b><small>systems</small></div>
          <div className="mtile"><b>{caseIds.length}</b><small>cases</small></div>
          <div className="mtile"><b>{repeats}</b><small>repeats</small></div>
          <div className="mtile" style={{ borderColor: "var(--accent)", background: "var(--accent-l)" }}><b>{total}</b><small>runs</small></div>
        </div>

        <div className="between" style={{ marginTop: 16 }}>
          <span className="muted">Queue-managed · persisted with full provenance.</span>
          <button className="btn primary" disabled={!!preparing || blocked || total === 0} onClick={launch}>Launch experiment →</button>
        </div>
      </div>

      {/* Overlay banner: shown while a launch loads its prompts; the error variant stays until closed. */}
      {preparing && (
        <div className="toast warn" role="status">
          <span><b>{PREPARING_LABEL}</b> {preparing.name} · pinning florence-prompts <span className="mono">{preparing.ref}</span></span>
        </div>
      )}
      {!preparing && launchErr && (
        <div className="toast bad" role="alert">
          <span>{launchErr}</span>
          <button className="toast-x" onClick={() => setLaunchErr(null)} aria-label="Close"><IconX size={14} /></button>
        </div>
      )}

      {launches.length > 0 && (
        <div className="card">
          <h3>Launched from this page</h3>
          {launches.map(({ id, name: launchName, job }) => {
            const failed = failedRuns(job);
            return (
              <div className="launch-row" key={id}>
                <span className="mono">{launchName}</span>
                <span className={`chip ${statusTone(job.status)}`} title={job.error || undefined}>{job.status}</span>
                <span className="muted">{job.done ?? 0}/{job.total ?? 0} runs</span>
                {failed > 0 && <span className="chip bad">{failed} failed</span>}
                {job.prompts_commit && <span className="chip n" title="All Mari runs use this florence-prompts commit">
                  prompts <CommitLink sha={job.prompts_commit} icon={false} /></span>}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
